import { Notification } from '@zambezi/sdk/notification';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { formatCurrency, pluralize } from '../../utils';
import AmountInput from '../AmountInput';
import Checkbox from '../Checkbox';
import Heading from '../Heading/Heading';
import ScreenReaderOnly from '../ScreenReaderOnly/ScreenReaderOnly';
import Tooltip from '../Tooltip';
import './SubAccountOverpayment.scss';

export type InterestType = 'F' | 'V' | 'T';

interface SubAccountOverpaymentProps {
  subAccountNumber: number | string;
  currentBalance: number;
  interestType: InterestType;
  interestRate: number;
  monthlyPayment: number;
  overpayment?: number;
  remainingTerm: {
    years: number;
    months: number;
  };
  index: number;
}

const SubAccountOverpayment = ({
  subAccountNumber,
  currentBalance,
  interestRate,
  interestType,
  monthlyPayment,
  overpayment,
  remainingTerm,
  index
}: SubAccountOverpaymentProps) => {
  const {
    control,
    setValue,
    watch,
    formState: { errors },
    trigger
  } = useFormContext();

  const getInterestTypeLabel = (interestType: InterestType) => {
    return {
      F: 'fixed',
      V: 'variable',
      T: 'tracker'
    }[interestType];
  };

  const { years, months } = remainingTerm;
  const remainingTermText = `${pluralize(years, 'year', 'years')} & ${pluralize(
    months,
    'month',
    'months'
  )}`;

  const overpaymentAmount = watch(`subAccounts.${index}.newOverpayment`);
  const isCancelled = watch(`subAccounts.${index}.cancelled`);

  return (
    <div className="zb-card | mss-sub-account-overpayment">
      <div className="zb-card-body">
        <div className="mss-sub-account-overpayment__header">
          <Heading level={4} as="h3">
            Sub-account {subAccountNumber}
          </Heading>
          <div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <span>
                Current balance{' '}
                <Tooltip content="The amount of capital left to repay for this sub-account of your mortgage. This figure doesn't include any accrued interest.">
                  <ScreenReaderOnly>
                    More information about current balance for sub-account {subAccountNumber}
                  </ScreenReaderOnly>
                </Tooltip>
              </span>
              <span className="mss-heading mss-heading-4 | mss-sub-account-overpayment__balance">
                {formatCurrency(currentBalance)}
              </span>
            </div>
            <span>{`${interestRate}% ${getInterestTypeLabel(interestType)}`}</span>
          </div>
        </div>

        <div className="mss-sub-account-overpayment__body">
          <div className="mss-sub-account-overpayment__item">
            <span className="mss-sub-account-overpayment__item-title">
              Current monthly payment
              <Tooltip content="The amount you pay each month for this part of your mortgage.">
                <ScreenReaderOnly>
                  More information about current monthly payment for sub-account {subAccountNumber}
                </ScreenReaderOnly>
              </Tooltip>
            </span>
            <span className="zb-heading2">{formatCurrency(monthlyPayment + overpayment)}</span>
            <span className="mss-sub-account-overpayment__ancillary-text">
              {overpayment ? `(inc. ${formatCurrency(overpayment)} overpayment)` : null}
            </span>
          </div>
          <div className="mss-sub-account-overpayment__item">
            <span className="mss-sub-account-overpayment__item-title">
              Term &amp; type
              <Tooltip
                content={
                  <>
                    <h3 className="zb-heading3">Term:</h3>
                    <p>
                      The number of years and months you have left to repay this sub-account of your
                      mortgage.
                    </p>

                    <h3 className="zb-heading3">Type:</h3>
                    <p>
                      Capital and Interest means your mortgage will be repaid by the end of the
                      repayment term if all payments are made. Interest Only means you must have a
                      plan in place to repay the remaining balance at the end of the repayment term.
                      If you have multiple sub-accounts in your mortgage there may be a mixture of
                      the two.
                    </p>
                  </>
                }
              >
                <ScreenReaderOnly>
                  More information about term and type for sub-account {subAccountNumber}
                </ScreenReaderOnly>
              </Tooltip>
            </span>
            <span className="zb-heading2">{remainingTermText}</span>
            <span className="mss-sub-account-overpayment__ancillary-text">
              Capital &amp; Interest
            </span>
          </div>
          <div className="mss-sub-account-overpayment__item">
            <span className="mss-sub-account-overpayment__item-title">
              New amount to overpay
              <Tooltip content="Please enter the new amount you want to overpay on this sub account. If you already make a regular overpayment the figure you provide here will replace your existing overpayment.">
                <ScreenReaderOnly>
                  More information about new amount to overpay for sub-account {subAccountNumber}
                </ScreenReaderOnly>
              </Tooltip>
            </span>
            <Controller
              render={({ field: { value, name, onBlur, ref }, fieldState: { error } }) => (
                <AmountInput
                  label="How much do you want to overpay on this sub account?"
                  onBlur={(val) => {
                    setValue(name, val);
                    onBlur();
                  }}
                  name={name}
                  value={value}
                  error={!!error?.message}
                  ref={ref}
                  disabled={isCancelled}
                  inputProps={{
                    'aria-describedby': !!error?.message && 'sub-account-error',
                    maxLength: 8
                  }}
                  hideLabel
                />
              )}
              name={`subAccounts.${index}.newOverpayment`}
              control={control}
            />
          </div>
          <div className="mss-sub-account-overpayment__item">
            <span className="mss-sub-account-overpayment__item-title">
              Select to cancel
              <Tooltip content="Please select this box if you wish to cancel your existing regular overpayment.">
                <ScreenReaderOnly>
                  More information about cancelling overpayment for sub-account {subAccountNumber}
                </ScreenReaderOnly>
              </Tooltip>
            </span>
            <Controller
              render={({
                field: { onChange, onBlur, value, name, ref },
                fieldState: { error }
              }) => (
                <Checkbox
                  label="Cancel your overpayment for this account"
                  onChange={(_, isChecked) => {
                    onChange(isChecked);
                    onBlur();
                    setValue(`subAccounts.${index}.newOverpayment`, overpayment);
                    trigger(`subAccounts.${index}.newOverpayment`);
                  }}
                  disabled={!overpayment}
                  error={error?.message}
                  name={name}
                  checked={value === true}
                  ref={ref}
                  hideLabel
                />
              )}
              name={`subAccounts.${index}.cancelled`}
              control={control}
            />
          </div>
        </div>
        {/* @ts-expect-error some error */}
        {errors?.subAccounts?.[index]?.newOverpayment ? (
          <div className="mss-sub-account-overpayment__error" id="sub-account-error">
            <Notification status="error" iconProps={{ title: '' }}>
              {/* @ts-expect-error some error */}
              {errors.subAccounts?.[index].newOverpayment?.message}
            </Notification>
          </div>
        ) : null}
      </div>
      {!!overpaymentAmount && overpaymentAmount !== overpayment && (
        <div className="mss-sub-account-overpayment__footer">
          <div>
            Total new monthly sub-account payment{' '}
            <ScreenReaderOnly>for sub-account {subAccountNumber}</ScreenReaderOnly>
          </div>
          <div className="zb-heading2">
            {formatCurrency(isCancelled ? monthlyPayment : overpaymentAmount + monthlyPayment)}
          </div>
        </div>
      )}
    </div>
  );
};

export default SubAccountOverpayment;
